import {
    Box,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import { ChevronRight } from '@mui/icons-material';
import GraphQLProposalRecordDAOFactory from '../../lib/proposal/dao/record/GraphQLProposalRecordDAOFactory';
import GraphQLSolutionDAOFactory from '../../lib/solution/majorVersion/dao/GraphQLSolutionDAOFactory';
import { OrganizationType } from '../../models';
import SearchCriteriaFactory from '../../lib/search/SearchCriteriaFactory';
import SearchIcon from '@mui/icons-material/Search';
import { Solution } from '../../models';
import { debounce } from 'lodash';
import { searchCriteriaState } from '../../lib/search/SearchCriteriaState';
import theme from '../../assets/style/theme';
import { useLocation } from "react-router-dom";
import { useRecoilState } from 'recoil';
import { useUser } from '../user/UserContext';

const SUPPORTED_PAGE_PATHS = ["/properties", "/team", "/admin", "/contacts"];
const PNUMBER_SEARCH_PATHS = ["/properties"];
const proposalDAO = GraphQLProposalRecordDAOFactory.getInstance();
const solutionDAO = GraphQLSolutionDAOFactory.getInstance();

const SearchBar = () => {
    const [searchCriteria, setSearchCriteria] = useRecoilState(searchCriteriaState);
    const searchFieldRef = useRef<HTMLInputElement>();
    const boxRef = useRef<HTMLDivElement>();
    const location = useLocation();
    const [showSolution, setShowSolution] = useState(false);
    const [loadingSolution, setLoadingSolution] = useState(false);
    const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
    const { accountType } = useUser();

    const showSearchBar = useMemo(() => {
        return SUPPORTED_PAGE_PATHS.some(path => location.pathname === path);
    }, [location.pathname]);

    const shouldPNumberSearch = useMemo((): boolean => {
        return accountType !== OrganizationType.PROPERTY_MANAGEMENT &&
            PNUMBER_SEARCH_PATHS.some(path => path === location.pathname);
    }, [location.pathname, accountType]);

    useEffect(() => {
        resetSearchCriteria();
    }, [location]);

    function resetSearchCriteria(): void {
        if (searchFieldRef?.current?.value) {
            searchFieldRef.current.value = '';
        }
        setSearchCriteria(SearchCriteriaFactory.getAllResultsCriteria());
    }

    const pNumberSearch = async (query: string): Promise<void> => {
        if (!shouldPNumberSearch) {
            return;
        }

        const proposalNumber = parseInt(query);

        setSelectedSolution(null);

        if (!isNaN(proposalNumber)) {
            setShowSolution(true);
            setLoadingSolution(true);

            try {
                const proposal = await proposalDAO.getByProposalNumber(proposalNumber);
                const solution = await solutionDAO.getById(proposal.designDocumentGroupId);

                setSelectedSolution(solution as Solution);
            } catch {
                setSelectedSolution(null);
            } finally {
                setLoadingSolution(false);
            }
        } else {
            setShowSolution(false);
        }
    };

    const handleChange = debounce((event) => {
        setSearchCriteria(prev => {
            return SearchCriteriaFactory.create(prev.globalSearch, event.target.value);
        });
        pNumberSearch(event.target.value);
    }, 300);

    if (!showSearchBar) {
        return (<></>);
    };

    return (
        <Box>
            <Box
                ref={boxRef}
                ml="auto"
                display="flex"
            >
                <TextField
                    variant='outlined'
                    size="small"
                    placeholder='Search'
                    InputProps={{
                        sx: {
                            "color": "text.secondary",
                            backgroundColor: "secondary.main",
                            px: 0,
                            "& .MuiInputBase-input": {
                                px: searchCriteria.keywords.length > 0 ? 1 : 0,
                                transition: theme.transitions.create('width'),
                                width: searchCriteria.keywords.length > 0 ? "20ch" : "0px",
                                '&:focus': {
                                    px: 1,
                                    width: "20ch",
                                },
                            }
                        },
                        endAdornment:
                            <InputAdornment position="end" sx={{ mx: 0 }}>
                                <IconButton onClick={() => searchFieldRef.current?.focus()}>
                                    <SearchIcon fontSize="inherit" />
                                </IconButton>
                            </InputAdornment>
                    }}
                    inputRef={searchFieldRef}
                    onChange={handleChange}
                    onFocus={(): Promise<void> => pNumberSearch(searchCriteria.keywords)}
                />
            </Box>
            <ClickAwayListener
                onClickAway={(event: MouseEvent | TouchEvent): void => {
                    if (event.target !== searchFieldRef.current) {
                        setShowSolution(false);
                    }
                }}
                sx={{
                    width: `${boxRef.current?.clientWidth ?? 0}px`
                }}
            >
                <Box
                    position="relative"
                    width="100%"
                >
                    {showSolution && (
                        <Box
                            bgcolor="white"
                            borderRadius="4px"
                            position="absolute"
                            marginTop="8px"
                            width="100%"
                            paddingY="8px"
                            boxShadow="4"
                        >
                            {loadingSolution && (
                                <Typography
                                    width="100%"
                                    textAlign="center"
                                    color="gray"
                                >
                                    Loading...
                                </Typography>
                            )}
                            {!loadingSolution && !!selectedSolution && (
                                <MenuItem
                                    sx={{
                                        paddingX: "8px",
                                        paddingY: "2px",
                                    }}
                                >
                                    <Link
                                        href={`${window.origin}/properties/${selectedSolution.propertyId}/solutions/${selectedSolution.id}/view`}
                                        underline="none"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        width="100%"
                                        maxWidth="100%"
                                    >
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                        >
                                            <Typography
                                                fontWeight="500"
                                            >
                                                Proposal #{searchCriteria.keywords}
                                            </Typography>
                                            <Typography
                                                color="black"
                                            >
                                                Go to this proposal
                                            </Typography>
                                        </Box>
                                        <ChevronRight fontSize="small" />
                                    </Link>
                                </MenuItem>
                            )}
                            {!loadingSolution && !selectedSolution && (
                                <Typography
                                    width="100%"
                                    textAlign="center"
                                    color="gray"
                                >
                                    No Solution Found
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </ClickAwayListener>
        </Box>
    );
};

export default SearchBar;
